/**
 * Posting ↔ Desktop Bridge
 *
 * Replicates FOM's bidirectional sync between fom_daily_posting and fom_personnel.
 *
 * mergePostingToPersonnel() — called after every cloud pull
 *   Reads fom_daily_posting, maps supervisors/officers → fom_personnel entries.
 *   Preserves existing equipment (key, radio, recorder, badge).
 *   Also updates fom_secondaryTasks, fom_supervisors, fom_deskOfficer, fom_notes.
 *
 * syncPersonnelToPosting() — called before every cloud push
 *   Reads fom_personnel, converts back to fom_daily_posting format.
 *   Preserves existing posting fields (notes, date, shift).
 */

// ── Helpers ────────────────────────────────────────────────────────────────

interface PostingEntry {
  cs: string
  name: string
  time: string
  isOIC: boolean
  posts: { post: string; from: string; to: string }[]
  tasks: { taskId: string; time: string }[]
  brk: { time: string; endTime: string; coveredBy: string }
}

interface PostingData {
  date: string
  shift: string
  supervisors: PostingEntry[]
  officers: PostingEntry[]
  notes: string
}

interface PersonnelEntry {
  role: string
  name: string
  callSign: string
  key: string
  radio: string
  recorder: string
  badge: string
  shiftFrom: string
  shiftTo: string
  posts: { post: string; from: string; to: string; isBreak: boolean; coveredBy?: string }[]
}

const ROLE_MAP: Record<string, string> = {
  'developer': 'Dir/AD/Lt',
  'dv': 'Dir/AD/Lt',
  'director of security': 'Dir/AD/Lt',
  'assistant director': 'Dir/AD/Lt',
  'lieutenant': 'Dir/AD/Lt',
  'sergeant': 'Sergeant',
  'sergeants': 'Sergeant',
  'oic': 'OIC',
  'officer': 'Officer',
  'officers': 'Officer',
  'dispatcher': 'Dispatcher',
  'dispatchers': 'Dispatcher',
}

const TASK_LABELS: Record<string, string> = {
  'fe_upper': 'FE Upper', 'fe_lower': 'FE Lower',
  'detex': 'Detex', 'mall_doors': 'Mall Doors', 'j_ext': 'J Ext',
  'svc_doors': 'Svc Doors', 'esc_tv': 'Esc/TV',
  'cny': 'Lunar New Year',
}

const TASK_CATEGORIES: Record<string, string> = {
  'fe_upper': 'Fire Extinguishers', 'fe_lower': 'Fire Extinguishers',
  'detex': 'Mall Inspection', 'mall_doors': 'Mall Doors', 'j_ext': 'J Ext',
  'svc_doors': 'Service Doors', 'esc_tv': 'Escalators',
  'cny': 'Chinese Lunar New Year',
}

function localToday(): string {
  try {
    const s = new Date().toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit', hour12: false,
    })
    const [date] = s.split(',')
    const [m, d, y] = date.trim().split('/')
    return `${y}-${m}-${d}`
  } catch {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
}

// ── Equipment persistence ─────────────────────────────────────────────────
// Saves and restores per-officer equipment assignments independent of
// posting merges. Never wiped by resetShift or mergePostingToPersonnel.
export function saveEquipmentAssignment(callSign: string, eq: { radio?: string; badge?: string; key?: string; recorder?: string }) {
  if (!callSign) return
  try {
    const map = JSON.parse(localStorage.getItem('fom_equipment_assignments') ?? '{}')
    map[callSign.toUpperCase()] = { ...(map[callSign.toUpperCase()] ?? {}), ...eq }
    localStorage.setItem('fom_equipment_assignments', JSON.stringify(map))
  } catch { /* silent */ }
}

export function loadEquipmentAssignment(callSign: string): { radio: string; badge: string; key: string; recorder: string } | null {
  if (!callSign) return null
  try {
    const map = JSON.parse(localStorage.getItem('fom_equipment_assignments') ?? '{}')
    return map[callSign.toUpperCase()] ?? null
  } catch { return null }
}

function lookupRoleFromRoster(cs: string): { role: string; rank: string } | null {
  try {
    const roster = JSON.parse(localStorage.getItem('fom_roster') ?? '{}') as Record<string, { name: string; callSign?: string }[]>
    const csUp = cs.toUpperCase().trim()
    for (const cat in roster) {
      if (!Array.isArray(roster[cat])) continue
      for (const entry of roster[cat]) {
        if (
          (entry.callSign && entry.callSign.toUpperCase() === csUp) ||
          (entry.name && entry.name.toUpperCase().trim() === csUp)
        ) {
          const mapped = ROLE_MAP[cat.toLowerCase()]
          if (mapped) return { role: mapped, rank: cat }
          // Non-security categories — preserve their actual category
          return { role: cat, rank: cat }
        }
      }
    }
  } catch { /* silent */ }
  return null
}

// S-01 through S-08 are always Supervisors (call-sign authority rule).
// SC-* call signs are always FCPD (police units like SC-81/SC-82 are not
// registered app users, so the roster lookup can never classify them —
// without this rule every posting merge silently demoted them to 'Officer',
// which made the FCPD badge vanish from the card and dashboard).
function roleFromCallSign(cs: string): string | null {
  if (!cs) return null
  const up = cs.toUpperCase()
  if (/^SC-?\d+$/.test(up)) return 'FCPD'
  const m = up.match(/^S-?0*([1-9]\d*)$/)
  if (!m) return null
  const n = parseInt(m[1])
  if (n >= 1 && n <= 8) return 'Sergeant'
  return 'Officer'
}

function convertPosts(posts: { post: string; from: string; to: string }[]): PersonnelEntry['posts'] {
  if (!Array.isArray(posts)) return []
  return posts.map(p => ({
    post: p.post ?? '',
    from: p.from ?? '',
    to: p.to ?? '',
    isBreak: (p.post ?? '').toLowerCase() === 'break',
  }))
}

// ── Remove single officer from posting ───────────────────────────────────────
// Called when a slot is explicitly cleared in Personnel on Duty.
// Strips the matching row from fom_daily_posting so mergePostingToPersonnel
// does not restore the person on the next cloud pull.
export function removeFromPosting(callSign: string): void {
  if (!callSign) return
  try {
    const cs = callSign.toUpperCase()
    // Clear any saved equipment assignment for this person. Without this, the
    // record lingers in fom_equipment_assignments and buildSlot() restores all
    // their old equipment (key/radio/recorder/badge) the next time they are
    // re-added — the "everything comes back" bug.
    try {
      const map = JSON.parse(localStorage.getItem('fom_equipment_assignments') ?? '{}')
      if (map[cs]) { delete map[cs]; localStorage.setItem('fom_equipment_assignments', JSON.stringify(map)) }
    } catch { /* silent */ }

    const raw = localStorage.getItem('fom_daily_posting')
    if (!raw) return
    const posting = JSON.parse(raw) as PostingData
    const inSup = (posting.supervisors ?? []).some(s => s.cs?.toUpperCase() === cs)
    const inOfc = (posting.officers   ?? []).some(o => o.cs?.toUpperCase() === cs)
    if (!inSup && !inOfc) return
    if (inSup) posting.supervisors = (posting.supervisors ?? []).filter(s => s.cs?.toUpperCase() !== cs)
    if (inOfc) posting.officers   = (posting.officers   ?? []).filter(o => o.cs?.toUpperCase() !== cs)
    localStorage.setItem('fom_daily_posting', JSON.stringify(posting))
    // Invalidate merge hash so next mergePostingToPersonnel sees the removal
    localStorage.removeItem('fom_last_posting_merge')
    try { window.dispatchEvent(new CustomEvent('castellan:posting-changed')) } catch { /* silent */ }
  } catch { /* silent */ }
}

// ── Posting → Desktop ──────────────────────────────────────────────────────

export function mergePostingToPersonnel(): boolean {
  try {
    const postingRaw = localStorage.getItem('fom_daily_posting')
    if (!postingRaw) return false

    // If shift was reset, only block if the posting is from the OLD shift.
    // If the posting date/shift matches TODAY's current shift, always allow
    // (supervisor may have just filled in the new shift posting on mobile).
    const resetKey = localStorage.getItem('fom_reset_shift_key')
    if (resetKey) {
      try {
        const etStr = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false })
        const [hStr, mStr] = etStr.split(':')
        const totalMins = parseInt(hStr) * 60 + parseInt(mStr)
        const currentShift = totalMins >= 6*60+55 && totalMins < 14*60+55 ? 'Morning'
                           : totalMins >= 14*60+55 && totalMins < 22*60+55 ? 'Evening' : 'Night'
        const currentDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
        const currentKey = `${currentDate}-${currentShift}`
        // Only block if the posting is for a DIFFERENT shift than now
        // Allow if the posting matches the current shift (new shift being entered on mobile)
        const postingShift = (() => { try { return (JSON.parse(postingRaw) as any).shift ?? '' } catch { return '' } })()
        if (resetKey === currentKey && postingShift !== currentShift) return false
      } catch { /* silent */ }
    }

    const posting: PostingData = JSON.parse(postingRaw)
    if (!posting.supervisors && !posting.officers) return false

    // Hash guard — skip if nothing changed AND personnel already has data
    const postingHash = buildPostingHash(postingRaw, posting)
    const lastHash = localStorage.getItem('fom_last_posting_merge') ?? ''
    const existingPersonnel = localStorage.getItem('fom_personnel')
    const personnelHasNames = (() => {
      try {
        const arr = JSON.parse(existingPersonnel ?? '[]') as { name?: string }[]
        return arr.some(p => !!p.name)
      } catch { return false }
    })()
    if (postingHash === lastHash && personnelHasNames) return false

    // Existing personnel for equipment preservation
    const existingMap: Record<string, PersonnelEntry> = {}
    try {
      const existing: PersonnelEntry[] = JSON.parse(localStorage.getItem('fom_personnel') ?? '[]')
      existing.forEach(p => {
        if (p.callSign) existingMap[p.callSign.toUpperCase()] = p
      })
    } catch { /* silent */ }

    // Roster lookup for default equipment
    const rosterLookup: Record<string, { radioNumber?: string; badgeNumber?: number }> = {}
    try {
      const roster = JSON.parse(localStorage.getItem('fom_roster') ?? '{}') as Record<string, any[]>
      Object.values(roster).flat().forEach((r: any) => {
        if (r.name) rosterLookup[r.name.toLowerCase()] = r
        if (r.callSign) rosterLookup[r.callSign.toUpperCase()] = r
      })
    } catch { /* silent */ }

    // Current session for role fallback
    let session: { callSign?: string; rank?: string } | null = null
    try { session = JSON.parse(localStorage.getItem('fom_auth_session') ?? 'null') } catch { /* silent */ }

    const allPosted: {
      callSign: string; name: string; role: string;
      shiftFrom: string; shiftTo: string;
      posts: PersonnelEntry['posts']; tasks: { taskId: string; time: string }[]
    }[] = []

    // Process supervisors
    for (const s of (posting.supervisors ?? [])) {
      if (!s.cs) continue
      let role = 'Sergeant'
      const reg = lookupRoleFromRoster(s.cs) ?? (s.name ? lookupRoleFromRoster(s.name) : null)
      if (reg) role = reg.role === 'Officer' ? 'Sergeant' : reg.role
      if (!reg && session?.callSign && s.cs.toUpperCase() === session.callSign.toUpperCase()) {
        const r = (session.rank ?? '').toLowerCase()
        role = ROLE_MAP[r] ?? 'Sergeant'
      }
      if (s.isOIC) role = 'OIC'

      const posts = convertPosts(s.posts ?? [])
      if (s.brk?.time) posts.push({ post: 'Break', from: s.brk.time, to: s.brk.endTime ?? '', isBreak: true, coveredBy: s.brk.coveredBy ?? '' })

      // Name: use posting name, or look up from roster by callSign
      let name = s.name ?? ''
      if (!name) {
        const rEntry = rosterLookup[s.cs.toUpperCase()]
        if (rEntry && (rEntry as any).name) name = (rEntry as any).name
      }

      const [shiftFrom, shiftTo] = (s.time ?? '').split('-')
      allPosted.push({ callSign: s.cs, name, role, shiftFrom: shiftFrom ?? '', shiftTo: shiftTo ?? '', posts, tasks: s.tasks ?? [] })
    }

    // Process officers
    for (const o of (posting.officers ?? [])) {
      if (!o.cs) continue
      // Apply call-sign authority rule FIRST: S-01–S-08 = Supervisor
      const csRole = roleFromCallSign(o.cs)
      let role = csRole ?? 'Officer'
      const reg = lookupRoleFromRoster(o.cs) ?? (o.name ? lookupRoleFromRoster(o.name) : null)
      if (reg && csRole === null) role = reg.role // only use roster if call-sign rule didn't fire
      else if (reg && csRole !== null && reg.role !== 'Officer') role = reg.role // roster rank wins over generic
      if (o.isOIC) role = 'OIC'

      const posts = convertPosts(o.posts ?? [])
      if (o.brk?.time) posts.push({ post: 'Break', from: o.brk.time, to: o.brk.endTime ?? '', isBreak: true, coveredBy: o.brk.coveredBy ?? '' })

      // Name: use posting name, or look up from roster by callSign
      let name = o.name ?? ''
      if (!name) {
        const rEntry = rosterLookup[o.cs.toUpperCase()]
        if (rEntry && (rEntry as any).name) name = (rEntry as any).name
      }

      const [shiftFrom, shiftTo] = (o.time ?? '').split('-')
      allPosted.push({ callSign: o.cs, name, role, shiftFrom: shiftFrom ?? '', shiftTo: shiftTo ?? '', posts, tasks: o.tasks ?? [] })
    }

    // ── NOTES ── always sync notes from posting → fom_notes, even when
    // personnel is unchanged. Posting notes are the source of truth when
    // a posting blob exists.
    {
      let notesArr: string[] = []
      if (posting.notes) {
        const raw = typeof posting.notes === 'string' ? posting.notes.split('\n') : posting.notes as unknown as string[]
        notesArr = raw.map(n => n.replace(/^[•·\-*]\s*\t?/, '').trim()).filter(Boolean)
      }
      const newNotesStr = JSON.stringify(notesArr)
      if (newNotesStr !== localStorage.getItem('fom_notes')) {
        localStorage.setItem('fom_notes', newNotesStr)
      }
    }

    if (allPosted.length === 0) return false

    // Build new personnel list — ALWAYS as 9-slot structure (3 sups + 6 officers)
    // matching the desktop normalizer expectations
    function buildSlot(posted: typeof allPosted[0] | null, fallbackRole: string): PersonnelEntry {
      if (!posted) {
        return { role: fallbackRole, name: '', callSign: '', key: '', radio: '', recorder: '', badge: '', shiftFrom: '', shiftTo: '', posts: [] }
      }
      const csUp = posted.callSign.toUpperCase()
      const existing = existingMap[csUp]
      const rosterEntry = rosterLookup[csUp] ?? rosterLookup[(posted.name ?? '').toLowerCase()]
      // Only use saved equipment assignments if the officer was ALREADY in
      // the previous personnel. Never auto-assign based on old test sessions.
      const savedEq = existing ? loadEquipmentAssignment(posted.callSign) : null
      // Resolve posts: start from the posting (which already has the break appended
      // from brk above if brk.time was set). If the posting has no break but the
      // existing personnel entry does, preserve the existing break rather than
      // silently dropping it.
      const baseResolvedPosts = posted.posts.length > 0
        ? posted.posts
        : (existing?.posts ?? [])
      const postingHasBreak = baseResolvedPosts.some(p => p.isBreak || p.post === 'Break')
      const existingBreak   = !postingHasBreak
        ? existing?.posts?.find(p => p.isBreak || p.post === 'Break')
        : undefined
      const resolvedPosts = existingBreak
        ? [...baseResolvedPosts, existingBreak]
        : baseResolvedPosts
      return {
        role: fallbackRole,
        name: posted.name,
        callSign: posted.callSign,
        shiftFrom: posted.shiftFrom,
        shiftTo: posted.shiftTo,
        // Equipment: if savedEq exists (supervisor explicitly set assignments),
        // use it even when a field is empty string (empty = supervisor cleared it).
        // Only fall back to existing/roster when there is NO savedEq record at all.
        // The old || operator treated '' as falsy, causing cleared equipment to
        // silently fall back to stale personnel values on every merge.
        key:      savedEq != null ? (savedEq.key      ?? '') : (existing?.key      ?? ''),
        radio:    savedEq != null ? (savedEq.radio    ?? '') : (existing?.radio    ?? (rosterEntry?.radioNumber ? String(rosterEntry.radioNumber) : '')),
        recorder: savedEq != null ? (savedEq.recorder ?? '') : (existing?.recorder ?? ''),
        badge:    savedEq != null ? (savedEq.badge    ?? '') : (existing?.badge    ?? (rosterEntry?.badgeNumber ? String(rosterEntry.badgeNumber) : '')),
        posts: resolvedPosts,
      }
    }
    const dirEntries = allPosted.filter(p => p.role === 'Dir/AD/Lt')
    const sgtEntries = allPosted.filter(p => p.role === 'Sergeant')
    const oicEntries = allPosted.filter(p => p.role === 'OIC')
    const ofcEntries = allPosted.filter(p => p.role === 'Officer')

    // Order the Dir/AD/Lt group by true rank precedence so the Director always
    // renders above the Assistant Director, who renders above the Lieutenant.
    // They all share the 'Dir/AD/Lt' role, so without this the order is just
    // whatever sequence they happened to appear in the posting array (which
    // put S-02 above S-01). Resolve precedence from the roster category, then
    // fall back to call-sign number ascending.
    const RANK_PRECEDENCE: Record<string, number> = {
      'Director of Security': 1, 'Assistant Director': 2, 'Lieutenant': 3, 'Developer': 4,
    }
    function rankOf(p: { callSign: string; name: string }): number {
      const reg = lookupRoleFromRoster(p.callSign) ?? (p.name ? lookupRoleFromRoster(p.name) : null)
      const byCat = reg ? RANK_PRECEDENCE[reg.rank] : undefined
      if (byCat) return byCat
      const m = (p.callSign ?? '').toUpperCase().match(/^S-?0*([1-9]\d*)$/)
      return m ? 100 + parseInt(m[1]) : 999
    }
    dirEntries.sort((a, b) => rankOf(a) - rankOf(b))

    const newPersonnel: PersonnelEntry[] = []
    newPersonnel.push(buildSlot(dirEntries[0] ?? null, 'Dir/AD/Lt'))
    newPersonnel.push(buildSlot(sgtEntries[0] ?? null, 'Sergeant'))
    newPersonnel.push(buildSlot(oicEntries[0] ?? null, 'OIC'))
    // Extra supervisors (e.g. FOM has Director + AD + Lieutenant all mapping to
    // 'Dir/AD/Lt', plus multiple Sergeants) overflow past the 3 fixed supervisor
    // slots. They must KEEP a supervisor role so PersonnelSection's SUP_ROLES_SET
    // classifies them as supervisors and the officer-badge auto-assigner skips
    // them. Previously these were forced to 'Officer', which made S-02/S-03
    // render as officers and receive officer badges. Preserve each extra's own
    // supervisor role instead.
    const extras = [...dirEntries.slice(1), ...sgtEntries.slice(1), ...oicEntries.slice(1)]
    for (const e of extras) newPersonnel.push(buildSlot(e, e.role))
    for (const o of ofcEntries) newPersonnel.push(buildSlot(o, 'Officer'))

    // Preserve FCPD officers that exist in the current personnel but are NOT in
    // the posting. FCPD units (SC-81/SC-82) are toggled directly into personnel
    // and are never part of the daily posting form, so a posting-driven rebuild
    // would otherwise drop them entirely — the bug where FCPD officers vanish
    // shortly after being toggled on. Carry them forward verbatim.
    try {
      const postedCS = new Set(allPosted.map(p => (p.callSign || '').toUpperCase()))
      const existingArr: PersonnelEntry[] = JSON.parse(localStorage.getItem('fom_personnel') ?? '[]')
      for (const e of existingArr) {
        const isFcpd = e.role === 'FCPD' || /^SC-?\d+$/i.test(e.callSign || '')
        if (isFcpd && e.name && !postedCS.has((e.callSign || '').toUpperCase())) {
          newPersonnel.push({ ...e, role: 'FCPD' })
        }
      }
    } catch { /* silent */ }

    while (newPersonnel.filter(p => p.role === 'Officer').length < 6) {
      newPersonnel.push(buildSlot(null, 'Officer'))
    }

    // Check if anything actually changed
    const newStr = JSON.stringify(newPersonnel)
    if (newStr === localStorage.getItem('fom_personnel')) return false

    localStorage.setItem('fom_personnel', newStr)
    localStorage.setItem('fom_last_posting_merge', postingHash)

    // Update secondary tasks
    const allTasks: any[] = []
    allPosted.forEach(p => {
      (p.tasks ?? []).forEach(t => {
        const tid = t.taskId ?? ''
        allTasks.push({
          callSign: p.callSign,
          task: TASK_CATEGORIES[tid] ?? tid,
          label: TASK_LABELS[tid] ?? tid,
          detail: TASK_LABELS[tid] ?? tid,
          time: t.time ?? '',
        })
      })
    })
    if (allTasks.length > 0) localStorage.setItem('fom_secondaryTasks', JSON.stringify(allTasks))

    // Update shift info
    if (posting.date || posting.shift) {
      localStorage.setItem('fom_currentDate', posting.date || localToday())
      try {
        const summary = JSON.parse(localStorage.getItem('fom_security_summary') ?? '{}')
        summary.date = posting.date || summary.date || ''
        summary.shift = posting.shift || summary.shift || ''
        localStorage.setItem('fom_security_summary', JSON.stringify(summary))
      } catch { /* silent */ }
    }

    // Set supervisors list
    const supNames = allPosted
      .filter(p => ['Developer', 'Dir/AD/Lt', 'Sergeant', 'OIC'].includes(p.role))
      .map(p => p.name)
      .filter(Boolean)
    if (supNames.length > 0) localStorage.setItem('fom_supervisors', JSON.stringify(supNames))

    // Set desk officer
    allPosted.forEach(p => {
      (p.posts ?? []).forEach(post => {
        if (post.post?.toLowerCase().includes('desk')) {
          localStorage.setItem('fom_deskOfficer', p.name)
        }
      })
    })

    // Notes already synced above (before personnel diff early-return)

    return true
  } catch (e) {
    console.error('mergePostingToPersonnel error:', e)
    return false
  }
}

// ── Desktop → Posting ──────────────────────────────────────────────────────

export function syncPersonnelToPosting(): void {
  try {
    const personnelRaw = localStorage.getItem('fom_personnel')
    if (!personnelRaw) return
    const personnel: PersonnelEntry[] = JSON.parse(personnelRaw)
    if (!personnel.length) return

    const namedPersonnel = personnel.filter(p => !!p.name)
    if (namedPersonnel.length === 0) return

    // Read existing posting — we only UPDATE existing rows, never replace the whole form.
    // This preserves blank rows the user just added (no cs yet) and any rows D1 doesn't know about.
    const existingRaw = localStorage.getItem('fom_daily_posting')
    if (!existingRaw) return
    const existing: PostingData = JSON.parse(existingRaw)

    const tasksRaw = localStorage.getItem('fom_secondaryTasks')
    const allTasks: any[] = tasksRaw ? JSON.parse(tasksRaw) : []

    const LABEL_TO_ID: Record<string, string> = {
      'Fire Extinguishers': 'fe_upper', 'Mall Inspection': 'detex',
      'Mall Doors': 'mall_doors', 'Service Doors': 'svc_doors',
      'J Ext': 'j_ext', 'Escalators': 'esc_tv',
    }

    // Build a lookup of personnel by call sign for fast matching
    const personnelByCS: Record<string, PersonnelEntry> = {}
    namedPersonnel.forEach(p => {
      if (p.callSign) personnelByCS[p.callSign.toUpperCase()] = p
    })

    function buildEntryFromPersonnel(p: PersonnelEntry): PostingEntry {
      const personTasks = allTasks.filter(
        (t: any) => t.callSign && p.callSign && t.callSign.toUpperCase() === p.callSign.toUpperCase()
      )
      const taskList = personTasks.map((t: any) => ({
        taskId: LABEL_TO_ID[t.task] ?? t.task ?? '',
        time: t.time ?? '',
      }))
      const posts = (p.posts ?? [])
        .filter(x => !x.isBreak && x.post !== 'Break')
        .map(x => ({ post: x.post ?? '', from: x.from ?? '', to: x.to ?? '' }))
      const brkPost = (p.posts ?? []).find(x => x.isBreak || x.post === 'Break')
      const time = (p.shiftFrom && p.shiftTo) ? `${p.shiftFrom}-${p.shiftTo}` : ''
      return {
        cs: p.callSign ?? '',
        name: p.name ?? '',
        time,
        posts,
        tasks: taskList,
        brk: brkPost
          ? { time: brkPost.from ?? '', endTime: brkPost.to ?? '', coveredBy: brkPost.coveredBy ?? '' }
          : { time: '', endTime: '', coveredBy: '' },
        isOIC: p.role === 'OIC',
      }
    }

    // Update EXISTING posting rows where we have matching personnel — never delete rows
    const updatedSups = (existing.supervisors ?? []).map(row => {
      if (!row.cs) return row // blank row — preserve as-is
      const match = personnelByCS[row.cs.toUpperCase()]
      if (!match) return row // no personnel match — preserve as-is
      const built = buildEntryFromPersonnel(match)
      return {
        ...built,
        cs: row.cs,
        posts: built.posts.length > 0 ? built.posts : row.posts,
        tasks: built.tasks.length > 0 ? built.tasks : row.tasks,
        // Break rule:
        //   - Posting already has a break → keep it (don’t let stale personnel overwrite)
        //   - Posting has no break but personnel does → write personnel’s break back to
        //     the posting so the NEXT merge can propagate it to mobile via brk.time
        brk: row.brk.time ? row.brk : (built.brk?.time ? built.brk : row.brk),
      }
    })

    const updatedOfcs = (existing.officers ?? []).map(row => {
      if (!row.cs) return row // blank row — preserve as-is
      const match = personnelByCS[row.cs.toUpperCase()]
      if (!match) return row // no personnel match — preserve as-is
      const built = buildEntryFromPersonnel(match)
      return {
        ...built,
        cs: row.cs,
        posts: built.posts.length > 0 ? built.posts : row.posts,
        tasks: built.tasks.length > 0 ? built.tasks : row.tasks,
        brk: row.brk.time ? row.brk : (built.brk?.time ? built.brk : row.brk),
      }
    })

    // ── Add NEW personnel who have no posting row yet ───────────────────────────
    // The map() above only updates rows that already exist in the posting.
    // Anyone added directly in Personnel on Duty (not via the posting form)
    // had no row to update and was silently dropped. Find those people and
    // insert a fresh row into the correct posting section.
    const SUP_ROLES_POSTING = new Set([
      'Dir/AD/Lt', 'Sergeant', 'OIC', 'Developer',
      'Lieutenant', 'Director of Security', 'Assistant Director',
    ])
    const postingCSSet = new Set<string>()
    updatedSups.forEach(s => { if (s.cs) postingCSSet.add(s.cs.toUpperCase()) })
    updatedOfcs.forEach(o => { if (o.cs) postingCSSet.add(o.cs.toUpperCase()) })

    for (const p of namedPersonnel) {
      if (!p.callSign) continue
      if (postingCSSet.has(p.callSign.toUpperCase())) continue
      // In Personnel on Duty but missing from posting — add a fresh row
      const newEntry = buildEntryFromPersonnel(p)
      if (SUP_ROLES_POSTING.has(p.role)) {
        updatedSups.push(newEntry)
      } else {
        updatedOfcs.push(newEntry)  // covers Officer, FCPD, Dispatcher, etc.
      }
    }

    const newPosting: PostingData = {
      date: existing.date || localToday(),
      shift: existing.shift || '',
      supervisors: updatedSups,
      officers: updatedOfcs,
      // Notes: the posting form is the source of truth. Never overwrite with fom_notes
      // (which may be empty [] after a reset) — that caused typed notes to disappear
      // every time the subscriber fired.
      notes: existing.notes ?? '',
    }

    const newStr = JSON.stringify(newPosting)
    if (newStr === localStorage.getItem('fom_daily_posting')) return
    localStorage.setItem('fom_daily_posting', newStr)
  } catch (e) {
    console.error('syncPersonnelToPosting error:', e)
  }
}

// ── Hashing ────────────────────────────────────────────────────────────────

function buildPostingHash(raw: string, posting: PostingData): string {
  const supPart = (posting.supervisors ?? []).map(s =>
    `${s.cs}/${s.time}/${s.name}/${JSON.stringify(s.posts)}/${JSON.stringify(s.brk)}`
  ).join(',')
  const ofcPart = (posting.officers ?? []).map(o =>
    `${o.cs}/${o.time}/${o.name}/${JSON.stringify(o.posts)}/${JSON.stringify(o.brk)}`
  ).join(',')
  return `${raw.length}-${posting.date}-${posting.shift}-${supPart}-${ofcPart}-${JSON.stringify(posting.notes)}`
}
